"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.EIP2930AccessListTransaction = void 0;
const utils_1 = require("@ganache/utils");
const ethereum_address_1 = require("@ganache/ethereum-address");
const rlp_1 = require("@ganache/rlp");
const runtime_transaction_1 = require("./runtime-transaction");
const access_lists_1 = require("./access-lists");
const signing_1 = require("./signing");
const ganache_ethereum_utils_1 = require("@soloseng/ganache-ethereum-utils");
const CAPABILITIES = [2718, 2930];
class EIP2930AccessListTransaction extends runtime_transaction_1.RuntimeTransaction {
    constructor(data, common, extra) {
        super(data, common, extra);
        this.type = utils_1.Quantity.from("0x1");
        if (Array.isArray(data)) {
            this.chainId = utils_1.Quantity.from(data[0]);
            this.nonce = utils_1.Quantity.from(data[1]);
            this.gasPrice = this.effectiveGasPrice = utils_1.Quantity.from(data[2]);
            this.gas = utils_1.Quantity.from(data[3]);
            this.to = data[4].length == 0 ? null : ethereum_address_1.Address.from(data[4]);
            this.value = utils_1.Quantity.from(data[5]);
            this.data = utils_1.Data.from(data[6]);
            const accessListData = access_lists_1.AccessLists.getAccessListData(data[7]);
            this.accessList = accessListData.accessList;
            this.accessListJSON = accessListData.AccessListJSON;
            this.accessListDataFee = accessListData.dataFeeEIP2930;
            this.v = utils_1.Quantity.from(data[8]);
            this.r = utils_1.Quantity.from(data[9]);
            this.s = utils_1.Quantity.from(data[10]);
            this.raw = data;
            if (!extra) {
                // TODO(hack): we use the presence of `extra` to determine if this data
                // come from the "database" or not. Transactions that come from the
                // database must not be validated since they may come from a fork.
                if (common.chainId() !== this.chainId.toBigInt()) {
                    throw new ganache_ethereum_utils_1.CodedError(`Invalid chain id (${this.chainId.toBigInt()}) for chain with id ${common.chainId()}.`, utils_1.JsonRpcErrorCode.INVALID_INPUT);
                }
                const { from, serialized, hash } = this.computeIntrinsics(this.v, this.raw);
                this.from = from;
                this.serialized = serialized;
                this.hash = hash;
            }
        }
        else {
            if (data.chainId) {
                this.chainId = utils_1.Quantity.from(data.chainId);
                if (this.common.chainId() !== this.chainId.toBigInt()) {
                    throw new ganache_ethereum_utils_1.CodedError(`Invalid chain id (${this.chainId.toNumber()}) for chain with id ${common.chainId()}.`, utils_1.JsonRpcErrorCode.INVALID_INPUT);
                }
            }
            else {
                this.chainId = utils_1.Quantity.from(common.chainId());
            }
            this.gasPrice = this.effectiveGasPrice = utils_1.Quantity.from(data.gasPrice);
            const accessListData = access_lists_1.AccessLists.getAccessListData(data.accessList);
            this.accessList = accessListData.accessList;
            this.accessListJSON = accessListData.AccessListJSON;
            this.accessListDataFee = accessListData.dataFeeEIP2930;
            this.validateAndSetSignature(data);
        }
    }
    maxGasPrice() {
        return this.gasPrice;
    }
    toJSON(_common) {
        return {
            hash: this.hash,
            type: this.type,
            chainId: this.chainId,
            nonce: this.nonce,
            blockHash: this.blockHash ? this.blockHash : null,
            blockNumber: this.blockNumber ? this.blockNumber : null,
            transactionIndex: this.index ? this.index : null,
            from: this.from,
            to: this.to,
            value: this.value,
            gas: this.gas,
            gasPrice: this.gasPrice,
            input: this.data,
            accessList: this.accessListJSON,
            v: this.v,
            r: this.r,
            s: this.s
        };
    }
    static fromTxData(data, common, extra) {
        return new EIP2930AccessListTransaction(data, common, extra);
    }
    toVmTransaction() {
        const data = this.data.toBuffer();
        return {
            hash: () => utils_1.BUFFER_32_ZERO,
            nonce: this.nonce.toBigInt(),
            common: this.common,
            gasPrice: this.gasPrice.toBigInt(),
            gasLimit: this.gas.toBigInt(),
            to: this.to,
            value: this.value.toBigInt(),
            data,
            AccessListJSON: this.accessListJSON,
            getSenderAddress: () => this.from,
            /**
             * the minimum amount of gas the tx must have (DataFee + TxFee + Creation Fee)
             */
            getBaseFee: () => {
                return this.calculateIntrinsicGas();
            },
            getUpfrontCost: () => {
                const { gas, gasPrice, value } = this;
                return gas.toBigInt() * gasPrice.toBigInt() + value.toBigInt();
            },
            supports: (capability) => {
                return CAPABILITIES.includes(capability);
            }
        };
    }
    calculateIntrinsicGas() {
        return super.calculateIntrinsicGas() + this.accessListDataFee;
    }
    /**
     * sign a transaction with a given private key, then compute and set the `hash`.
     *
     * @param privateKey - Must be 32 bytes in length
     */
    signAndHash(privateKey) {
        if (this.v != null) {
            throw new Error("Internal Error: RuntimeTransaction `sign` called but transaction has already been signed");
        }
        const raw = this.toEthRawTransaction(utils_1.BUFFER_ZERO, utils_1.BUFFER_ZERO, utils_1.BUFFER_ZERO);
        const data = (0, rlp_1.encodeRange)(raw, 0, 8);
        const dataLength = data.length;
        const msgHash = (0, utils_1.keccak)((0, signing_1.digestWithPrefix)(1, [data.output], dataLength));
        const sig = (0, utils_1.ecsign)(msgHash, privateKey);
        this.v = utils_1.Quantity.from(sig.v);
        this.r = utils_1.Quantity.from(sig.r);
        this.s = utils_1.Quantity.from(sig.s);
        raw[8] = this.v.toBuffer();
        raw[9] = this.r.toBuffer();
        raw[10] = this.s.toBuffer();
        this.raw = raw;
        const encodedSignature = (0, rlp_1.encodeRange)(raw, 8, 3);
        const ranges = [data.output, encodedSignature.output];
        const length = dataLength + encodedSignature.length;
        // serialized is type concatenated with the rest of the data rlp encoded
        this.serialized = (0, signing_1.digestWithPrefix)(1, ranges, length);
        this.hash = utils_1.Data.from((0, utils_1.keccak)(this.serialized));
    }
    toEthRawTransaction(v, r, s) {
        return [
            this.chainId.toBuffer(),
            this.nonce.toBuffer(),
            this.gasPrice.toBuffer(),
            this.gas.toBuffer(),
            this.to ? this.to.toBuffer() : utils_1.BUFFER_EMPTY,
            this.value.toBuffer(),
            this.data.toBuffer(),
            this.accessList,
            v,
            r,
            s
        ];
    }
    computeIntrinsics(v, raw) {
        return (0, signing_1.computeIntrinsicsAccessListTx)(v, raw);
    }
    updateEffectiveGasPrice() { }
}
exports.EIP2930AccessListTransaction = EIP2930AccessListTransaction;
//# sourceMappingURL=eip2930-access-list-transaction.js.map